<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="close">
    <div class="modal-card">
      <div class="section-header">
        <h3>Merge Contact</h3>
        <button class="icon-button" type="button" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <p v-if="error" class="banner banner-error">{{ error }}</p>

        <template v-if="step === 'pick'">
          <p class="merge-intro">
            Merging into <strong>{{ primaryContact?.contact_name }}</strong>. Pick the duplicate contact to absorb —
            its leads, messages, notes, reminders, and quotes will move over, and the duplicate record will be deleted.
          </p>

          <label class="modal-field">
            <span>Search contacts</span>
            <input v-model="searchTerm" type="text" placeholder="Name, email, or phone" />
          </label>

          <div class="merge-results">
            <button
              v-for="contact in searchResults"
              :key="contact.id"
              type="button"
              class="merge-result-row"
              @click="selectSecondary(contact)"
            >
              <strong>{{ contact.contact_name }}</strong>
              <span>{{ contact.email || contact.phone || 'No contact info' }}</span>
            </button>

            <p v-if="!searchResults.length" class="merge-empty">No other contacts match.</p>
          </div>
        </template>

        <template v-else-if="step === 'resolve' && secondaryContact">
          <p class="merge-intro">
            Choose which value to keep for each field. Everything else from
            <strong>{{ secondaryContact.contact_name }}</strong> (leads, messages, notes, reminders, quotes) moves onto
            <strong>{{ primaryContact?.contact_name }}</strong>.
          </p>

          <div class="merge-compare">
            <div class="merge-compare-header">
              <span></span>
              <span>{{ primaryContact?.contact_name }}</span>
              <span>{{ secondaryContact.contact_name }}</span>
            </div>

            <div v-for="field in MERGE_FIELDS" :key="field.key" class="merge-field-row">
              <span class="merge-field-label">{{ field.label }}</span>
              <label class="merge-option" :class="{ active: resolved[field.key] === (primaryContact[field.key] || '') }">
                <input
                  type="radio"
                  :name="`merge-${field.key}`"
                  :checked="resolved[field.key] === (primaryContact[field.key] || '')"
                  @change="resolved[field.key] = primaryContact[field.key] || ''"
                />
                <span>{{ primaryContact[field.key] || '—' }}</span>
              </label>
              <label class="merge-option" :class="{ active: resolved[field.key] === (secondaryContact[field.key] || '') }">
                <input
                  type="radio"
                  :name="`merge-${field.key}`"
                  :checked="resolved[field.key] === (secondaryContact[field.key] || '')"
                  @change="resolved[field.key] = secondaryContact[field.key] || ''"
                />
                <span>{{ secondaryContact[field.key] || '—' }}</span>
              </label>
            </div>
          </div>
        </template>
      </div>

      <div class="modal-actions">
        <button v-if="step === 'resolve'" class="icon-button" type="button" @click="step = 'pick'">Back</button>
        <button class="icon-button" type="button" @click="close">Cancel</button>
        <button
          v-if="step === 'resolve'"
          class="send-button"
          type="button"
          :disabled="!resolved.contact_name?.trim() || sending"
          @click="confirmMerge"
        >
          {{ sending ? 'Merging…' : 'Merge contacts' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'

const MERGE_FIELDS = [
  { key: 'contact_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'province', label: 'Province' },
  { key: 'postal_code', label: 'Postal Code' },
  { key: 'notes', label: 'Notes' },
]

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  primaryContact: { type: Object, default: null },
  contacts: { type: Array, default: () => [] },
  companyId: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'merged'])

const step = ref('pick')
const searchTerm = ref('')
const secondaryContact = ref(null)
const resolved = ref({})
const sending = ref(false)
const error = ref('')

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    step.value = 'pick'
    searchTerm.value = ''
    secondaryContact.value = null
    resolved.value = {}
    error.value = ''
  }
)

const searchResults = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()
  return props.contacts
    .filter((contact) => contact.id !== props.primaryContact?.id)
    .filter((contact) => {
      if (!term) return true
      return [contact.contact_name, contact.email, contact.phone]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    })
    .slice(0, 20)
})

function selectSecondary(contact) {
  secondaryContact.value = contact
  const draft = {}
  for (const field of MERGE_FIELDS) {
    draft[field.key] = props.primaryContact[field.key] || contact[field.key] || ''
  }
  resolved.value = draft
  step.value = 'resolve'
}

function close() {
  emit('update:modelValue', false)
}

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function confirmMerge() {
  if (!props.primaryContact || !secondaryContact.value || sending.value) return

  sending.value = true
  error.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${props.primaryContact.id}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        merge_contact_id: secondaryContact.value.id,
        ...resolved.value,
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to merge contacts.')
    }

    const token = await getAuthToken()
    logAction(token, {
      action: 'contact.merge',
      label: `Merged ${secondaryContact.value.contact_name} into ${data.contact.contact_name}`,
      company_id: props.companyId,
      metadata: { kept_id: data.contact.id, merged_id: secondaryContact.value.id },
    })

    emit('merged', { keptContact: data.contact, mergedId: secondaryContact.value.id })
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err.message || 'Failed to merge contacts.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
}

.send-button {
  min-width: 130px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  align-self: end;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.send-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 35, 31, 0.45);
}

.modal-card {
  width: min(560px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 24px 60px rgba(23, 35, 31, 0.25);
}

.modal-body {
  display: grid;
  gap: 12px;
  padding: 16px;
  overflow: auto;
}

.modal-field {
  display: grid;
  gap: 5px;
}

.modal-field span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.modal-field input {
  height: 40px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
}

.merge-intro {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.5;
}

.merge-results {
  display: grid;
  gap: 6px;
  max-height: 260px;
  overflow: auto;
}

.merge-result-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  text-align: left;
  cursor: pointer;
}

/* #f3faf7 is a fixed light tint, not theme-reactive — pin the row's own
   text (the bare <strong>) and its .span child both to a fixed dark pair,
   or they inherit var(--text-primary)/var(--text-secondary) and go
   near-white on a light background in dark mode. */
.merge-result-row:hover {
  border-color: #9fc8ba;
  background: #f3faf7;
  color: #17231f;
}

.merge-result-row span {
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.merge-result-row:hover span {
  color: #56635e;
}

.merge-empty {
  padding: 10px 2px;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.merge-compare {
  display: grid;
  gap: 4px;
}

.merge-compare-header {
  display: grid;
  grid-template-columns: 96px 1fr 1fr;
  gap: 8px;
  padding: 0 2px 4px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.merge-field-row {
  display: grid;
  grid-template-columns: 96px 1fr 1fr;
  gap: 8px;
  align-items: stretch;
}

.merge-field-label {
  align-self: center;
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 700;
}

.merge-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  font-size: 0.84rem;
  color: var(--text-primary);
  cursor: pointer;
  word-break: break-word;
}

.merge-option.active {
  border-color: #1f7a63;
  background: #f3faf7;
  color: #17231f;
}

.merge-option input {
  flex: 0 0 auto;
}
</style>
